/**
 * Top-right (top-centre on mobile) toast stack.
 *
 * Replaces alert() for non-blocking feedback. Errors persist until dismissed;
 * success/info auto-dismiss after ~4s.
 */

type ToastKind = 'success' | 'error' | 'info';

let stack: HTMLDivElement | null = null;

function ensureStack(): HTMLDivElement {
  if (stack) return stack;
  const root = document.getElementById('mp-toast-root');
  if (root instanceof HTMLDivElement) {
    stack = root;
    return root;
  }
  const div = document.createElement('div');
  div.id = 'mp-toast-root';
  document.body.appendChild(div);
  stack = div;
  return div;
}

export function toast(kind: ToastKind, message: string, opts: { persist?: boolean } = {}): void {
  const root = ensureStack();
  const persist = opts.persist ?? kind === 'error';

  const el = document.createElement('div');
  el.className = `toast toast-${kind}`;
  el.setAttribute('role', kind === 'error' ? 'alert' : 'status');

  const icon = document.createElement('span');
  icon.className = 'toast-icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = kind === 'success' ? '✓' : kind === 'error' ? '!' : 'i';

  const body = document.createElement('span');
  body.className = 'toast-body';
  body.textContent = message;

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'toast-close';
  close.setAttribute('aria-label', 'Dismiss');
  close.textContent = '×';
  close.onclick = () => dismiss();

  el.append(icon, body, close);
  root.appendChild(el);

  // animate in
  requestAnimationFrame(() => el.classList.add('in'));

  let timer: number | undefined;
  const dismiss = (): void => {
    if (timer !== undefined) clearTimeout(timer);
    el.classList.remove('in');
    el.classList.add('out');
    setTimeout(() => el.remove(), 200);
  };
  if (!persist) {
    timer = window.setTimeout(dismiss, 4000);
  }
}
